import { AnimatePresence, motion } from "framer-motion";
import {
  Fragment,
  type KeyboardEvent,
  type ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { CheckIcon, ChevronRightIcon, EllipsisIcon } from "lucide-react";

import {
  type DocumentFragment,
  RuleDefinitionSuggestionMatchDocumentFragment,
  SettlementTreatmentEnum,
  TagColorEnum,
  TriageSuggestionStatusEnum,
} from "@/__generated__/graphql";
import { HotkeyDecorator } from "@/components/atoms/HotkeyDecorator";
import { SimpleEnum } from "@/components/molecules/SimpleEnum";
import { Icon } from "@/components/atoms/Icon";
import { Popper } from "@/components/atoms/Popper";
import { List } from "@/components/organisms/List";
import { useSelectableListStyle } from "@/components/molecules/SelectableList";
import { getSelectableRowInset } from "@/components/molecules/SelectableRow";
import { Queries } from "@/constants";
import { useBackendQuery } from "@/hooks/useBackendQuery";
import { useResizeObserver } from "@/hooks/useResizeObserver";
import { Utils } from "@/utils";

import { icons } from "@/objects/@icons";
import { DocumentObject } from "@/objects/DocumentObject";
import { State } from "@/state";
import { Spinner } from "@/components/ui/spinner";
import { Slot } from "radix-ui";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";

import {
  INTER_ENTITY_ACCOUNTS,
  offsetLines,
  TriageBusinessArrow,
  TriageBusinessChip,
  TriageCategoryDot,
  TriageChip,
  TriageMovedChip,
  type TriageMovedPayment,
  TriageLinkBall,
  TriageCard,
  TriageDocumentCard,
  formatAccountPath,
  proposalSuggestedAccounts,
  type SuggestionLine,
} from "./TriageReconcileRow";
import {
  TriageAccountPostingsDialog,
  type TriagePostedAccount,
} from "./TriageAccountPostingsDialog";
import { TriageDuplicateDialog } from "./TriageDuplicateDialog";
import {
  TRIAGE_MAILBOX_FIND_PLACEHOLDERS,
  TriageDraftMatchDetails,
  TriageDraftMatchStop,
  TriageSearchMailboxStop,
} from "./TriageNoDocumentOptions";
import {
  TriageSuggestionContext,
  triageSuggestionReasons,
} from "./TriageSuggestionContext";
import {
  type TriageOptionListItem,
  TriageOptionSelectionControl,
} from "./TriageSuggestionsPanel";
import { categoryEnumColor } from "./TriageReconcileRow";
import {
  useTriageSettlementTreatments,
  type TriageSettlementTreatment,
} from "./TriageSettlement";
import { ruleWhenDescriptionSearchText } from "../RulesView/RuleWhenDescription";
import {
  countMatchDocumentCriteriaBuckets,
  matchDocumentFindCriteriaLines,
} from "./TriageMatchDocumentCriteriaCards";
import {
  JOB_ACTIONS,
  JOB_ICONS,
  JOB_LABELS,
  TRIAGE_KEYS,
  type TriageJobEnum,
} from "./TriageView.keys";
import {
  commitActiveOption,
  toggleLinesSuggestion,
  TriageDetailContext,
  useTriageDetailContext,
  useTriageViewActions,
  type TriageDetail,
  type TriageSuggestionStub,
} from "./TriageView.context";

type SuggestionMetadata = {
  configured_criteria?: string[];
  find?: { tier?: string; signals?: string[] };
  match_document?: { tier?: string; signals?: string[] };
  categorize?: {
    strategy?: string | null;
    match_on?: string | null;
    lookback_days?: number | null;
    signals?: string[];
  };
  duplicate?: { signals?: string[] };
  split?: { method?: string; part_count?: number; signals?: string[] };
  transfer?: { peer_count?: number; signals?: string[] };
  pair?: { peer_count?: number; signals?: string[] };
};

function splitAllocationTitleParts(
  suggestion: TriageSuggestionStub,
  triage: TriageDetail,
): ProposalSettlementLinePart[] {
  const offsets = offsetLines(suggestion, triage);

  // The accounts in words ("Meals and Travel"); the amounts are in the reason.
  const parts: ProposalSettlementLinePart[] = [];
  offsets.forEach((line, index) => {
    if (index > 0) {
      parts.push({
        kind: "faded",
        text: index === offsets.length - 1 ? " and " : ", ",
      });
    }
    const accountName =
      line.chartedAccount?.name?.trim() ||
      line.catalogueAccountName ||
      line.description;
    if (line.chartedAccount?.id || line.catalogueAccountName) {
      parts.push({
        kind: "account",
        nodeId: line.chartedAccount?.id ?? null,
        name: accountName,
        path: formatAccountPath(
          line.chartedAccount?.fullPath ?? line.catalogueAccountPath,
        ),
        testId: `triage-proposal-split-account-${index}`,
      });
    } else {
      parts.push({ kind: "emphasis", text: accountName });
    }
  });

  return parts;
}

export type ChangelistProposalPresentation = {
  job: TriageJobEnum;
  /** The other business it books in, by its own lines - nothing else decides whose it is. */
  otherBusiness: ProposalSettlementEntity | null;
  /**
   * For another business, what the payer posts - which business it lends to
   * and its own lines - that suggestions differing only in what is posted
   * there share: one card, each way to post it there a choice on it.
   */
  payerSide: string | null;
  searchText: string;
  confidenceLabel: string | null;
  rationale: string | null;
  selectionRationale: string | null;
  configuredCriteria: string[];
  matchedCriteria: string[];
  ruleName: string | null;
  matchDocumentDefinition: RuleDefinitionSuggestionMatchDocumentFragment | null;
  linkedDocuments: Array<{
    id: string;
    identifier: string;
    title: string;
    reference?: string | null;
    typeLabel?: string | null;
    documentType?: string | null;
    description?: string | null;
    contactName?: string | null;
    entityIdentifier?: string | null;
    amountLabel?: string | null;
    amountTotal?: number | null;
    amountDue?: number | null;
    /** The tax it charges, as its lines carry it. */
    amountTax?: number | null;
    dateIssued?: string | null;
    dateDue?: string | null;
    createdAt?: string | null;
    dateLabel?: string | null;
    contact?: {
      __typename: "ContactObject";
      id: string;
      identifier: string;
      name: string;
    } | null;
    panelNodeId?: string | null;
    /** Who emailed it in: its forwarder, or the sender in a connected mailbox. */
    fromEmailAddress?: string | null;
    /** The mailbox it was found in (``GMAIL``, ``OUTLOOK``); else forwarded. */
    externalProvider?: string | null;
    /** Whose it is: said beside who emailed it, where it is another business's. */
    entity?: { identifier?: string | null; name?: string | null } | null;
    /** The currency its amounts are in. */
    currency?: string | null;
    /**
     * Where that is not the payment's: the official rate between them the day
     * the payment posted - a unit of its in the payment's - and whose it is.
     * Its amounts are turned by it to be set beside the payment's.
     */
    exchangeRate?: number | null;
    exchangeRateSource?: string | null;
  }>;
  /**
   * Each document it matches that other payments pay already: those payments,
   * each with its share of it, and whether all of it is paid. Both - this and
   * those - pay it only where this fits in what is left: booked otherwise, it
   * would be paid past what it is for, which booking refuses.
   */
  paidElsewhere: Array<{
    document: { identifier: string; name?: string | null };
    shares: Array<{
      amount: number;
      transaction: NonNullable<
        NonNullable<
          NonNullable<
            TriageSuggestionStub["ruleSuggestionDocumentOptions"]
          >[number]["document"]
        >["paymentShares"]
      >[number]["transaction"];
    }>;
    isPaidInFull: boolean;
    /** Marked paid by hand, or by an integration, with no payment booked to it: this one is what paid it. */
    isMarkedByHand?: boolean;
  }>;
  /** Whether this and the payments paying its documents already may all pay them; else the matches move here. */
  canBothPay: boolean;
  /**
   * Whether the matches may move here: each payment paying its documents is
   * of a business the person is in. Another's is moved off only by someone in
   * both.
   */
  canMoveHere: boolean;
  headline: string;
  /** A document match's tier, said plainly ("Strong match"). */
  matchStrength: string | null;
};

/** A document match's tier, in the words the server's reason uses. */
const MATCH_TIER_LABELS: Record<string, string> = {
  exact_id: "Exact match",
  strong: "Strong match",
  fuzzy: "Likely match",
};

const CRITERIA_LABELS: Record<string, string> = {
  amount: "Amount",
  date_window: "Date",
  wording: "Wording",
  invoice_number: "Invoice #",
  reference: "Reference",
  external_id: "External ID",
  currency: "Currency",
  contact: "Contact",
  settlement: "Settlement",
  match_on: "Match on",
  lookback: "Lookback",
  description: "Description",
  same_account: "Same account",
  exclude_reconciled: "Exclude reconciled",
  entity_match: "Business named",
  target_entity: "Other business",
  min_amount: "Min amount",
  document_match: "Document match",
  remainder: "Remainder",
  account_pairs: "Account pairs",
  fx: "FX",
  strategy_fixed: "Fixed",
  strategy_merchant_history: "Merchant history",
  strategy_entity_frequency: "Business history",
  method_equal: "Equal split",
  method_by_percentage: "By %",
  method_by_amount: "By amount",
  method_manual: "Manual",
};

export function criteriaLabel(id: string): string {
  return CRITERIA_LABELS[id] ?? id.replace(/_/g, " ");
}

function inferJob(suggestion: TriageSuggestionStub): TriageJobEnum {
  if ((suggestion.ruleSuggestionDocumentOptions?.length ?? 0) > 0) {
    return "MATCH_DOCUMENT";
  }
  if (suggestion.job) {
    return suggestion.job as TriageJobEnum;
  }
  if ((suggestion.ruleSuggestionTransactionOptions?.length ?? 0) > 0) {
    return "DUPLICATE";
  }
  const lineCount = suggestion.lines?.length ?? 0;
  if (lineCount > 2) {
    return "SPLIT";
  }
  if (suggestion.ruleSuggestionChartedAccountOption?.chartedAccount) {
    return "CATEGORIZE";
  }
  return "CATEGORIZE";
}

function parseMetadata(suggestion: TriageSuggestionStub): SuggestionMetadata {
  const raw = suggestion.metadata;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return {};
  }
  return raw as SuggestionMetadata;
}

function matchedSignalsForJob(
  job: string,
  metadata: SuggestionMetadata,
  suggestion: TriageSuggestionStub,
): string[] {
  const fromMeta =
    job === "MATCH_DOCUMENT"
      ? (metadata.find?.signals ?? metadata.match_document?.signals)
      : job === "CATEGORIZE"
        ? metadata.categorize?.signals
        : job === "DUPLICATE"
          ? metadata.duplicate?.signals
          : job === "SPLIT"
            ? metadata.split?.signals
            : job === "TRANSFER"
              ? metadata.transfer?.signals
              : job === "PAIR"
                ? metadata.pair?.signals
                : undefined;
  if (fromMeta && fromMeta.length > 0) {
    return fromMeta;
  }

  const inferred: string[] = [];
  if (
    (suggestion.ruleSuggestionDocumentOptions ?? []).some((row) => row.document)
  ) {
    inferred.push("document");
  }
  const lines = (suggestion.lines ?? []) as SuggestionLine[];
  const txnAmount = lines.reduce(
    (sum, line) => sum + Number(line.amount ?? 0),
    0,
  );
  if (Math.abs(txnAmount) < 0.005 && lines.length >= 2) {
    inferred.push("amount");
  }
  return inferred;
}

function configuredCriteriaForJob(
  job: string,
  metadata: SuggestionMetadata,
  matched: string[],
): string[] {
  const configured = metadata.configured_criteria ?? [];
  if (configured.length > 0) {
    return configured;
  }
  return matched;
}

function buildSearchText(parts: Array<string | null | undefined>): string {
  return parts.filter(Boolean).join(" ").toLowerCase();
}

function criteriaMatchLabel(matched: number, total: number): string | null {
  if (total <= 0) {
    return null;
  }
  return `${Math.round((matched / total) * 100)}% match`;
}

function confidenceLabelForPresentation(
  job: TriageJobEnum,
  matchDocumentDefinition: RuleDefinitionSuggestionMatchDocumentFragment | null,
  configuredCriteria: string[],
  matchedCriteria: string[],
  matchDocument:
    | ChangelistProposalPresentation["linkedDocuments"][number]
    | null = null,
  bankAmount = 0,
  bankDate?: string | null,
): string | null {
  if (job === "MATCH_DOCUMENT" && matchDocumentDefinition != null) {
    const { matched, total } = countMatchDocumentCriteriaBuckets(
      matchDocumentDefinition,
      matchedCriteria,
      configuredCriteria,
      matchDocument,
      bankAmount,
      bankDate,
    );
    return criteriaMatchLabel(matched, total);
  }
  if (configuredCriteria.length === 0) {
    return null;
  }
  const matchedCount = configuredCriteria.filter((id) =>
    matchedCriteria.includes(id),
  ).length;
  return criteriaMatchLabel(matchedCount, configuredCriteria.length);
}

export function buildChangelistProposalPresentation(
  suggestion: TriageSuggestionStub,
  triage: TriageDetail,
): ChangelistProposalPresentation {
  const job = inferJob(suggestion);
  const metadata = parseMetadata(suggestion);
  const matchedCriteria = matchedSignalsForJob(job, metadata, suggestion);
  const configuredCriteria = configuredCriteriaForJob(
    job,
    metadata,
    matchedCriteria,
  );
  const rationale = suggestion.rationale?.trim() || null;
  const selectionRationale = suggestion.selectionRationale?.trim() || null;
  const ruleName = suggestion.rule?.name?.trim() || null;
  const whenDescriptionSearchText =
    job === "MATCH_DOCUMENT"
      ? null
      : ruleWhenDescriptionSearchText(suggestion.rule, job);
  const matchDocumentDefinition =
    job === "MATCH_DOCUMENT"
      ? (suggestion.matchDocumentDefinition ?? null)
      : null;

  const linkedDocuments = (
    suggestion.ruleSuggestionDocumentOptions ?? []
  ).flatMap((option) => {
    const document = option.document;
    if (!document?.identifier) return [];
    const reference = document.reference?.trim() || null;
    // Called as it is everywhere else - by what that reads of it.
    const title =
      DocumentObject.getTitle({
        name: document.name,
        reference: document.reference,
        description: document.description,
      } as DocumentFragment) ?? "Document";
    const contactName =
      document.contact?.organization?.name ||
      document.contact?.emailAddress ||
      null;
    const total = document.amountTotal ?? document.amountDue ?? null;
    const amountLabel =
      total != null ? Utils.fCurrency(Number(total), false, true) : null;
    const dateLabel = document.createdAt
      ? Utils.fDate(new Date(document.createdAt))
      : null;
    const fileName = document.file?.name?.trim() || "";
    const extension = fileName.includes(".")
      ? fileName.slice(fileName.lastIndexOf(".") + 1).toUpperCase()
      : "PDF";
    return [
      {
        id: document.id,
        identifier: document.identifier,
        title,
        reference,
        typeLabel: extension || "PDF",
        documentType: document.documentType ?? null,
        description: document.description?.trim() || null,
        contactName,
        contact:
          document.contact?.id && document.contact?.identifier
            ? {
                __typename: "ContactObject" as const,
                id: document.contact.id,
                identifier: document.contact.identifier,
                name: contactName || "Contact",
              }
            : null,
        entityIdentifier: document.entity?.identifier ?? null,
        amountLabel,
        amountTotal: document.amountTotal ?? null,
        amountTax: document.amountTax ?? null,
        amountDue: document.amountDue ?? null,
        dateIssued: document.dateIssued ?? null,
        dateDue: document.dateDue ?? null,
        createdAt: document.createdAt ?? null,
        dateLabel,
        panelNodeId: document.id,
        fromEmailAddress: document.fromEmailAddress ?? null,
        externalProvider: document.externalProvider ?? null,
        entity: document.entity ?? null,
        currency: document.amountCurrency ?? null,
        exchangeRate:
          option.exchangeRate != null ? Number(option.exchangeRate) : null,
        exchangeRateSource: option.exchangeRateSource ?? null,
      },
    ];
  });

  const txnAmount = Number(triage.amount ?? 0);
  const confidenceLabel = confidenceLabelForPresentation(
    job,
    matchDocumentDefinition,
    configuredCriteria,
    matchedCriteria,
    linkedDocuments[0] ?? null,
    txnAmount,
    triage.effectiveDate,
  );

  const offsets = offsetLines(suggestion, triage);

  const searchParts: Array<string | null | undefined> = [
    JOB_LABELS[job as TriageJobEnum] ?? job,
    ruleName,
    rationale,
    whenDescriptionSearchText,
    confidenceLabel,
    selectionRationale,
    ...configuredCriteria.map(criteriaLabel),
    ...matchedCriteria.map(criteriaLabel),
  ];
  if (job === "MATCH_DOCUMENT" && matchDocumentDefinition) {
    searchParts.push(
      ...matchDocumentFindCriteriaLines(
        matchDocumentDefinition,
        configuredCriteria,
      ),
    );
  }

  if (job === "MATCH_DOCUMENT") {
    searchParts.push(...linkedDocuments.map((doc) => doc.title));
  } else if (job === "CATEGORIZE") {
    const account =
      suggestion.ruleSuggestionChartedAccountOption?.chartedAccount ??
      offsets[0]?.chartedAccount;
    const category = suggestion.ruleSuggestionChartedAccountOption?.category;
    const categoryName = category?.name?.trim();
    const fullPath =
      account && "fullPath" in account && Array.isArray(account.fullPath)
        ? account.fullPath
        : undefined;
    const pathWithoutRoot = fullPath?.slice(1) ?? [];
    const accountPath =
      pathWithoutRoot.length > 0
        ? pathWithoutRoot.join(" / ")
        : account?.name?.trim();
    searchParts.push(accountPath, categoryName);
  } else if (job === "SPLIT") {
    searchParts.push(...offsets.map((line) => line.chartedAccount?.name));
  } else if (job === "DUPLICATE") {
    const peer = suggestion.ruleSuggestionTransactionOptions?.[0]?.transaction;
    const peerTransaction = peer as typeof peer & {
      effectiveDate?: string | null;
    };
    searchParts.push(
      peerTransaction?.description?.trim(),
      formatDuplicateComparisonDate(
        peerTransaction?.effectiveDate,
        peerTransaction?.externalCreatedAt,
      ),
    );
  } else if (job === "PAIR") {
    const peerTransactions = (
      suggestion.ruleSuggestionTransactionOptions ?? []
    ).flatMap((option) => {
      const peer = option.transaction as typeof option.transaction & {
        effectiveDate?: string | null;
      };
      if (!peer?.id) {
        return [];
      }
      return [
        {
          id: peer.id,
          description: peer.description,
          amount: peer.amount,
          effectiveDate: peer.effectiveDate,
          externalCreatedAt: peer.externalCreatedAt,
        },
      ];
    });
    const offsetName = offsets[0]?.chartedAccount?.name;
    searchParts.push(
      ...(offsetName ? [offsetName] : []),
      ...peerTransactions.flatMap((peer) => [
        peer.description?.trim(),
        formatDuplicateComparisonDate(
          peer.effectiveDate,
          peer.externalCreatedAt,
        ),
      ]),
    );
  } else if (job === "TRANSFER") {
    const peerTransactions = (
      suggestion.ruleSuggestionTransactionOptions ?? []
    ).flatMap((option) => {
      const peer = option.transaction as typeof option.transaction & {
        effectiveDate?: string | null;
      };
      if (!peer?.id) {
        return [];
      }
      return [
        {
          id: peer.id,
          description: peer.description,
          amount: peer.amount,
          effectiveDate: peer.effectiveDate,
          externalCreatedAt: peer.externalCreatedAt,
          accountName: peer.financialAccount?.name?.trim() || null,
        },
      ];
    });
    const clearingName = offsets[0]?.chartedAccount?.name ?? "Clearing account";
    searchParts.push(
      clearingName,
      ...peerTransactions.flatMap((peer) => [
        peer.description?.trim(),
        formatDuplicateComparisonDate(
          peer.effectiveDate,
          peer.externalCreatedAt,
        ),
      ]),
    );
  }

  // Whose it is: its lines say, and nothing else does.
  const lineRows = (suggestion.lines ?? []) as SuggestionLine[];
  const otherEntity = lineRows.find(
    (line) =>
      line.entity?.identifier &&
      line.entity.identifier !== triage.entity?.identifier,
  )?.entity;
  const otherBusiness = otherEntity?.identifier
    ? {
        id: otherEntity.id ?? null,
        identifier: otherEntity.identifier,
        name: otherEntity.name?.trim() || "another business",
      }
    : null;

  const payerSide = otherBusiness
    ? JSON.stringify([
        otherBusiness.identifier,
        lineRows
          .filter(
            (line) => line.entity?.identifier !== otherBusiness.identifier,
          )
          .map(
            (line) =>
              `${line.chartedAccount?.identifier ?? line.catalogueAccountName ?? ""}:${Number(line.amount ?? 0).toFixed(2)}`,
          )
          .sort(),
      ])
    : null;

  const paid = Math.abs(Number(triage.amount ?? 0));
  const owing =
    job === "MATCH_DOCUMENT"
      ? (suggestion.ruleSuggestionDocumentOptions ?? []).flatMap((option) => {
          const document = option.document;
          // In the payment's currency: a document in another turned at the
          // official rate - what each other payment settled of it too, in the
          // document's own currency, whatever that payment was in.
          const rate =
            option.exchangeRate != null ? Number(option.exchangeRate) : 1;
          const shares = (document?.paymentShares ?? [])
            .filter(
              (share) => share.transaction.identifier !== triage.identifier,
            )
            .map((share) => ({
              amount: Math.abs(Number(share.amountDocument ?? 0)) * rate,
              transaction: share.transaction,
            }));
          if (!document) return [];
          const total = Number(document.amountTotal ?? 0) * rate;
          if (shares.length === 0) {
            // Marked paid by hand with no payment of its own: this one is
            // what paid it, in the mark's place - said, nothing to choose.
            return total > 0 &&
              document.amountDue != null &&
              Number(document.amountDue) <= 0.005
              ? [
                  {
                    document: {
                      identifier: document.identifier,
                      name: document.name,
                    },
                    shares: [] as typeof shares,
                    isPaidInFull: true,
                    isMarkedByHand: true,
                    fits: true,
                  },
                ]
              : [];
          }
          // What is left of it to pay, as booking weighs it.
          const due =
            document.amountDue != null
              ? Number(document.amountDue) * rate
              : total - shares.reduce((sum, share) => sum + share.amount, 0);
          return [
            {
              document: {
                identifier: document.identifier,
                name: document.name,
              },
              shares,
              isPaidInFull: total > 0 && due <= 0.005,
              isMarkedByHand: false,
              fits: total <= 0 || due >= paid - 0.005,
            },
          ];
        })
      : [];

  return {
    job,
    otherBusiness,
    payerSide,
    paidElsewhere: owing.map(({ fits: _, ...one }) => one),
    canBothPay: owing.every((one) => one.fits),
    canMoveHere: owing.every((one) =>
      one.shares.every((share) =>
        State.getState().entities.some(
          (entity) =>
            entity.identifier === share.transaction.entity?.identifier,
        ),
      ),
    ),
    searchText: buildSearchText(searchParts),
    confidenceLabel,
    rationale,
    selectionRationale,
    configuredCriteria,
    matchedCriteria,
    ruleName,
    matchDocumentDefinition,
    linkedDocuments,
    matchStrength:
      job === "MATCH_DOCUMENT"
        ? (MATCH_TIER_LABELS[
            metadata.find?.tier ?? metadata.match_document?.tier ?? ""
          ] ?? null)
        : null,
    headline:
      job === "CATEGORIZE"
        ? (proposalSuggestedAccounts(suggestion, triage)[0]?.name ??
          proposalSuggestedCategory(suggestion)?.name ??
          changelistProposalHeadline(
            job,
            linkedDocuments.map((doc) => doc.title),
            rationale,
            ruleName,
          ))
        : changelistProposalHeadline(
            job,
            linkedDocuments.map((doc) => doc.title),
            rationale,
            ruleName,
          ),
  };
}

function changelistProposalJobLabel(job: string | null | undefined): string {
  if (!job) {
    return "Suggestion";
  }
  return (
    JOB_LABELS[job as TriageJobEnum] ??
    job.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())
  );
}

export function changelistProposalDisplayTitle(
  job: string | null | undefined,
  headline: string | null | undefined,
): string {
  const jobLabel = job ? changelistProposalJobLabel(job) : null;
  const headlineText = headline?.trim();
  if (!headlineText) {
    return jobLabel ?? "Suggestion";
  }
  return jobLabel ? `${jobLabel} · ${headlineText}` : headlineText;
}

/**
 * What a suggestion comes to, whoever proposed it: its kind, where each of its
 * lines posts and how much, and what it offers - the documents, the payments,
 * the category and tags. Two rules that reach the same conclusion are one row,
 * the rules counted on its chip.
 */
export function suggestionConclusion(suggestion: TriageSuggestionStub): string {
  const lines = (suggestion.lines ?? [])
    .map((line) =>
      [
        line.entity?.identifier ?? "",
        line.chartedAccount?.identifier ?? line.catalogueAccountName ?? "",
        Number(line.amount ?? 0).toFixed(2),
      ].join(":"),
    )
    .sort();
  const documents = (suggestion.ruleSuggestionDocumentOptions ?? [])
    .map((option) => option.document?.identifier ?? "")
    .sort();
  const transactions = (suggestion.ruleSuggestionTransactionOptions ?? [])
    .map((option) => option.transaction?.identifier ?? "")
    .sort();
  const tags = (suggestion.ruleSuggestionTagOptions ?? [])
    .map((option) => option.tag?.identifier ?? "")
    .sort();
  const category =
    suggestion.ruleSuggestionChartedAccountOption?.category?.identifier ?? "";
  return JSON.stringify([
    suggestion.job,
    lines,
    documents,
    transactions,
    tags,
    category,
  ]);
}

export function changelistProposalHeadline(
  job: string | null | undefined,
  linkedDocumentTitles: string[],
  rationale: string | null,
  ruleName: string | null,
): string {
  if (job === "MATCH_DOCUMENT") {
    const documentTitles = linkedDocumentTitles
      .map((title) => title.trim())
      .filter(Boolean);
    if (documentTitles.length > 0) {
      return documentTitles.join(", ");
    }
  }
  return (
    ruleName?.trim() ||
    rationale?.trim() ||
    changelistProposalJobLabel(job) ||
    "Suggestion"
  );
}

function formatDuplicateComparisonDate(
  effectiveDate?: string | null,
  externalCreatedAt?: string | null,
): string | null {
  const raw = effectiveDate ?? externalCreatedAt;
  if (!raw) {
    return null;
  }
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return Utils.fDate(date);
}

type ProposalSettlementEntity = {
  id: string | null;
  identifier: string;
  name: string;
};

function formatDocumentTypeLabel(type: string): string {
  return type
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function pluralizeDocumentTypeLabel(label: string): string {
  const parts = label.split(" ");
  const last = parts[parts.length - 1] ?? label;
  if (/s$/i.test(last)) {
    return label;
  }
  parts[parts.length - 1] = `${last}s`;
  return parts.join(" ");
}

function joinNaturalDocumentTypeList(items: string[]): string {
  if (items.length === 0) {
    return "documents";
  }
  if (items.length === 1) {
    return items[0];
  }
  if (items.length === 2) {
    return `${items[0]} and ${items[1]}`;
  }
  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
}

/**
 * What accepting a suggestion does, as its title says it - the verb first:
 * "Match receipt Staples #221", 'Post to "Expenses / … / Supplies"', "Split
 * across …", "Flag as a duplicate".
 */
function proposalCardTitleParts(
  suggestion: TriageSuggestionStub | undefined,
  presentation: ChangelistProposalPresentation,
  triage: TriageDetail,
): ProposalSettlementLinePart[] {
  if (presentation.job === "MATCH_DOCUMENT") {
    // Several documents are counted by type ("Match 2 invoices and a credit
    // note"); what the card opens to names each and adds them up.
    if (presentation.linkedDocuments.length > 1) {
      const counts = new Map<string, number>();
      for (const document of presentation.linkedDocuments) {
        const label = formatDocumentTypeLabel(
          document.documentType ?? "document",
        ).toLowerCase();
        counts.set(label, (counts.get(label) ?? 0) + 1);
      }
      const phrase = joinNaturalDocumentTypeList(
        [...counts].map(([label, count]) =>
          count === 1
            ? `${/^[aeiou]/.test(label) ? "an" : "a"} ${label}`
            : `${count} ${pluralizeDocumentTypeLabel(label)}`,
        ),
      );
      return [{ kind: "emphasis", text: `Match ${phrase}` }];
    }
    // One by its kind and its name - its number when it has no name.
    const [document] = presentation.linkedDocuments;
    if (document) {
      const kind = formatDocumentTypeLabel(
        document.documentType ?? "document",
      ).toLowerCase();
      const reference = document.reference?.trim();
      const name = document.title.trim() || (reference ? `#${reference}` : "");
      return [{ kind: "emphasis", text: `Match ${kind} ${name}`.trim() }];
    }
  }

  // A categorize is titled as a posting is, by its accounts - its category
  // chip says what it files it under. One with no account to name, by that.
  if (
    presentation.job === "CATEGORIZE" &&
    suggestion != null &&
    proposalSuggestedAccounts(suggestion, triage).length === 0
  ) {
    const category = proposalSuggestedCategory(suggestion);
    if (category != null) {
      return [{ kind: "emphasis", text: `Categorize as "${category.name}"` }];
    }
  }

  // What it is a copy of, by what that payment says.
  if (presentation.job === "DUPLICATE") {
    const copy =
      suggestion?.ruleSuggestionTransactionOptions?.[0]?.transaction?.description?.trim();
    if (copy) {
      return [{ kind: "emphasis", text: `Flag as a duplicate of ${copy}` }];
    }
  }

  if (presentation.job === "SPLIT" && suggestion != null) {
    const parts = splitAllocationTitleParts(suggestion, triage);
    if (parts.length > 0) {
      return [{ kind: "emphasis", text: "Split across " }, ...parts];
    }
  }

  // A transfer is named for what it is. The account it clears through is
  // bookkeeping plumbing (often Uncoded), and read as the title it said the
  // payment was being recorded as "a Uncoded".
  if (presentation.job === "TRANSFER") {
    const peer = suggestion?.ruleSuggestionTransactionOptions?.[0]
      ?.transaction as
      | {
          amount?: string | number | null;
          financialAccount?: { name?: string | null } | null;
        }
      | undefined;
    const here = triage.financialAccount?.name?.trim();
    const there = peer?.financialAccount?.name?.trim();
    if (here && there && peer?.amount != null) {
      const [from, to] =
        Number(triage.amount ?? 0) < 0 ? [here, there] : [there, here];
      return [{ kind: "emphasis", text: `Transfer from ${from} to ${to}` }];
    }
  }

  // Otherwise - a categorize with no category, a pair, a match with no
  // document linked - the accounts it posts the payment to, by name; one
  // accepting it makes says so after it. With none to name, what it does.
  const accounts =
    suggestion != null ? proposalSuggestedAccounts(suggestion, triage) : [];
  if (accounts.length === 0) {
    const action = JOB_ACTIONS[presentation.job];
    return [
      {
        kind: "emphasis",
        text: action.charAt(0).toUpperCase() + action.slice(1),
      },
    ];
  }
  return accounts.flatMap((account, index): ProposalSettlementLinePart[] => [
    {
      kind: "emphasis",
      text:
        index === 0
          ? 'Post to "'
          : index === accounts.length - 1
            ? ' and "'
            : ', "',
    },
    {
      kind: "account",
      nodeId: account.nodeId,
      name: account.name,
      path: account.path,
      testId:
        index === 0
          ? "triage-proposal-account"
          : `triage-proposal-account-${index}`,
    },
    { kind: "emphasis", text: account.isNew ? '" (new account)' : '"' },
  ]);
}

function proposalSuggestedCategory(
  suggestion: TriageSuggestionStub,
): { name: string; color: string | null } | null {
  const category = suggestion.ruleSuggestionChartedAccountOption?.category;
  const name = category?.name?.trim();
  if (!name) {
    // One the business has not made yet, made as it is accepted: said so.
    const proposed = suggestion.newCategoryName?.trim();
    return proposed ? { name: `${proposed} (new)`, color: null } : null;
  }
  return { name, color: category?.color ?? null };
}

type ProposalSettlementLinePart =
  | { kind: "faded"; text: string }
  | { kind: "entity"; nodeId: string | null; name: string; testId: string }
  | {
      kind: "account";
      nodeId: string | null;
      name: string;
      /** Where it sits in the chart, said under the title it is named in. */
      path: string;
      testId: string;
    }
  | { kind: "emphasis"; text: string };

/** The account a suggestion posts the payment to: the line that takes the money, or else its own pick. */
function postedAccountOf(
  suggestion: TriageSuggestionStub,
  triage: TriageDetail,
): TriagePostedAccount | null {
  // What its lines post to - what booking it books, and what a category it is
  // filed under must be of - before what its option names.
  const account =
    offsetLines(suggestion, triage).find((line) => line.chartedAccount)
      ?.chartedAccount ??
    suggestion.ruleSuggestionChartedAccountOption?.chartedAccount;
  return account?.identifier
    ? { identifier: account.identifier, name: account.name ?? "the account" }
    : null;
}

/**
 * A posting suggestion's category chip - what it files the payment under, C
 * to change it - wherever the suggestion is offered: its card, or the bulk
 * list's row of it. Changed, the suggestion is picked too (*onPick*), if it
 * was not. Nothing to file it under and nowhere to make one, no chip.
 */
export function TriageSuggestionCategoryChip({
  suggestion,
  isActive,
  onPick,
}: {
  suggestion: TriageSuggestionStub;
  /** Where the keyboard is: C opens it, which its badge says. */
  isActive: boolean;
  /** What picking the suggestion is where it is offered. */
  onPick: () => void;
}) {
  const { triage, selections, setSelection } = useTriageDetailContext();
  const isPicked =
    selections.linesSuggestionIdentifier === suggestion.identifier;
  // What it files under unless another is chosen on its chip.
  const suggested =
    suggestion.ruleSuggestionChartedAccountOption?.category ?? null;
  const account = postedAccountOf(suggestion, triage);
  return (
    <TriageProposalCategoryChip
      account={account}
      suggested={suggested}
      proposed={suggestion.newCategoryName?.trim() || null}
      named={isPicked ? selections.suggestionNewCategoryName : null}
      chosenIdentifier={
        isPicked ? selections.suggestionCategoryIdentifier : null
      }
      // A category named here is made under the account it posts to as it
      // is booked - one from the catalogue, made then too.
      onName={
        account != null ||
        offsetLines(suggestion, triage).some(
          (line) => line.catalogueAccountName,
        )
          ? (name) => {
              if (!isPicked) onPick();
              setSelection("suggestionCategoryIdentifier", null);
              setSelection("suggestionNewCategoryName", name);
            }
          : undefined
      }
      onChoose={(identifier) => {
        // Chosen, it is the one to book: picked, if it was not.
        if (!isPicked) onPick();
        setSelection(
          "suggestionCategoryIdentifier",
          identifier === suggested?.identifier ? null : identifier,
        );
        setSelection("suggestionNewCategoryName", null);
      }}
      triage={triage}
      isActive={isActive}
    />
  );
}

/**
 * What a posting files the payment under, as a chip: the category it suggests,
 * or the one chosen in its place. Clicked - or by C on the one the keyboard is
 * on - it opens on the categories of the account it posts to, to search, and
 * to make a new one under that account from what is typed; one chosen or made
 * there picks what it is on too - a suggestion, or a way to post it found for
 * what is typed, on its card or in Similar's bulk list - and is the one booked
 * with it.
 */
export function TriageProposalCategoryChip({
  account,
  suggested,
  proposed: wouldMake = null,
  named = null,
  chosenIdentifier,
  onChoose,
  onName,
  triage,
  isActive,
}: {
  /** What it posts to: what its categories are of, and where one named here is made. */
  account: TriagePostedAccount | null;
  /** What it is filed under unless another is chosen. */
  suggested: {
    id: string;
    identifier: string;
    name?: string | null;
    color?: TagColorEnum | null;
  } | null;
  /** One it would make as it is booked - until another is chosen. */
  proposed?: string | null;
  /** One the person named here, over all the rest: made as it is booked. */
  named?: string | null;
  /** The one chosen in the suggested one's place, where one is. */
  chosenIdentifier: string | null;
  /** Chosen here: picks what it is on as well. */
  onChoose: (identifier: string) => void;
  /**
   * Named here - a name its account has no category of: kept, to be made under
   * the account it posts to as it is booked, rather than made now - on the
   * chip at once, and nothing made that is never booked. Not given where it
   * posts to no account.
   */
  onName?: (name: string) => void;
  triage: TriageDetail;
  /** Where the keyboard is: C opens it, which its badge says. */
  isActive: boolean;
}) {
  const { isSearchFocused, journalSettlement } = useTriageDetailContext();
  const [isOpen, setIsOpen] = useState(false);
  // Where the keyboard was as it opened - the suggestions - given it back as it
  // shuts: the chip itself takes no focus.
  const focusedRef = useRef<HTMLElement | null>(null);
  const business = triage.entity?.identifier;
  // The accounts of the business that paid it, and of the one its cost is in
  // where its Move says it is another's: where what it posts to may be.
  const businesses = [
    ...new Set(
      [business, journalSettlement?.settlementEntityIdentifier].filter(
        (one): one is string => Boolean(one),
      ),
    ),
  ];
  const catalog = useBackendQuery(
    Queries.TRIAGE_CHARTED_ACCOUNT_CATALOG_QUERY,
    {
      variables: { entityIdentifiers: businesses },
      skip: !business || !account,
    },
  );
  const ofAccount =
    catalog.data?.chartedAccounts?.data?.find(
      (row) => row.identifier === account?.identifier,
    )?.categories ?? [];
  const categories = [
    ...(suggested ? [suggested] : []),
    ...ofAccount.filter(
      (category) => category.identifier !== suggested?.identifier,
    ),
  ];
  // One the person named, over the rest - and over what it suggests.
  const chosen = named
    ? null
    : (chosenIdentifier &&
        categories.find(
          (category) => category.identifier === chosenIdentifier,
        )) ||
      suggested;
  // One it would make, as it is accepted - until another is chosen.
  const proposed = named ?? (chosen ? null : wouldMake);
  // Nothing to file it under, and no account to make one in: no chip.
  if (!chosen && !proposed && !account && !onName) return null;
  const canChange = account != null || onName != null || categories.length > 1;
  // Its key, in the chip, on the one the keyboard is on - drawn narrower
  // than a button's, so the chip's end does not crowd it.
  const hotkey =
    canChange && isActive ? (
      <span className="flex [&_[data-testid='hotkey-decorator']]:pl-0.5 [&_[data-testid='hotkey-decorator']]:pr-0.5">
        <HotkeyDecorator
          // From the panel's search, after Escape leaves it - as a stop's
          // Enter is said.
          hotkey={
            isSearchFocused
              ? `escape+${TRIAGE_KEYS.CATEGORY}`
              : TRIAGE_KEYS.CATEGORY
          }
          color="inherit"
          size="xs"
        />
      </span>
    ) : undefined;
  return (
    <span
      // Its own: changing it is not a press of the stop or card it is on.
      data-selectable-row-inert=""
      onClick={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.preventDefault()}
      className="flex"
    >
      <Popper
        isOpenState={[isOpen, setIsOpen]}
        isClickable={false}
        placement="bottom-start"
        floatingStyle={{ width: 240 }}
        focusRestoreRef={focusedRef}
        anchor={
          <span
            role={canChange ? "button" : undefined}
            data-testid="triage-proposal-category"
            data-active={isActive}
            onClick={
              canChange
                ? () => {
                    if (!isOpen) {
                      focusedRef.current =
                        document.activeElement as HTMLElement | null;
                    }
                    setIsOpen(!isOpen);
                  }
                : undefined
            }
            className={cn(
              "flex items-center gap-1",
              canChange && "cursor-pointer",
            )}
          >
            {/* Drawn as the queue's row draws a category - offered here,
                so dashed - its colour a dot before its name. */}
            <TriageChip
              state="suggested"
              startDecorator={
                chosen ? <TriageCategoryDot color={chosen.color} /> : undefined
              }
              endDecorator={hotkey}
            >
              {chosen
                ? chosen.name
                : proposed
                  ? `${proposed} (new)`
                  : "Category…"}
            </TriageChip>
          </span>
        }
      >
        <div data-testid="triage-proposal-category-panel">
          <List<(typeof categories)[number]>
            data={categories}
            getRowKey={(category) => category.identifier}
            filterOptions={(options, filter) =>
              options.filter((option) =>
                (option.name ?? "")
                  .toLowerCase()
                  .includes(filter.trim().toLowerCase()),
              )
            }
            onItemClick={(category) => {
              setIsOpen(false);
              onChoose(category.identifier);
            }}
            onNewRow={
              onName
                ? (filter) => {
                    const name = filter.trim();
                    if (!name) return;
                    setIsOpen(false);
                    onName(name);
                  }
                : undefined
            }
            newRowNeedsName
            autofocus
            fillHeight={false}
            size="sm"
          >
            <List.SearchInput placeholder="Find or make a category…" />
            <List.Items<(typeof categories)[number]>>
              {(category: (typeof categories)[number]) => (
                <List.Item value={category}>
                  <div
                    data-testid="triage-proposal-category-option"
                    data-chosen={category.identifier === chosen?.identifier}
                    className="flex items-center gap-2 pl-1 pr-1 min-w-0"
                  >
                    <SimpleEnum
                      label={category.name ?? ""}
                      enumColor={categoryEnumColor(category.color)}
                      size="sm"
                      variant="outlined"
                    />
                    {category.identifier === chosen?.identifier ? (
                      <Icon ReactIcon={CheckIcon} size="sm" />
                    ) : null}
                  </div>
                </List.Item>
              )}
            </List.Items>
            <List.CreateNewFooter />
          </List>
        </div>
      </Popper>
    </span>
  );
}

/** A way two businesses may settle a payment, as a settlement chip lists it. */
type TriageSettlementWay = Pick<
  TriageSettlementTreatment,
  "treatment" | "title" | "description" | "isRecommended"
>;

/**
 * Something that books in another business too - a suggestion, or a move in
 * the bulk list: which business, the way the money went, and how the two
 * settle it - the one chip for it, on its card, that lists the ways it can be
 * settled - a suggestion's as the server would book each
 * (``settlementTreatments``), else the ways given (*ways*) - the one it
 * books unless another is picked first: a suggestion's said with it
 * (``recommendedTreatment``), so the chip says it from its first draw, not
 * once its ways are read. Picking one picks the card.
 */
export function TriageSettlementChip({
  suggestion,
  ways,
  business,
  isOut,
  chosen,
  moved,
  onChoose,
  testId = "triage-proposal-settlement",
  hotkey,
}: {
  /** The suggestion whose ways are read - none where *ways* are given. */
  suggestion: Pick<TriageSuggestionStub, "id" | "recommendedTreatment"> | null;
  ways?: TriageSettlementWay[];
  business: string;
  /** Paid out for it, rather than taken in. */
  isOut: boolean;
  /** The way picked here, where one is. */
  chosen: SettlementTreatmentEnum | null;
  /** The payment moved to this business already, settled this way: so it stands, nothing to choose. */
  moved: TriageMovedPayment | null;
  onChoose: (treatment: SettlementTreatmentEnum) => void;
  testId?: string;
  /** Its key, on it, where the keyboard is: it opens it (the view's M). */
  hotkey?: string;
}) {
  const { isSearchFocused } = useTriageDetailContext();
  const read = useTriageSettlementTreatments(suggestion?.id ?? null).treatments;
  const treatments: TriageSettlementWay[] = ways ?? read;
  const [isOpen, setIsOpen] = useState(false);
  const focusedRef = useRef<HTMLElement | null>(null);
  const shown =
    treatments.find((option) => option.treatment === chosen)?.treatment ??
    (suggestion
      ? suggestion.recommendedTreatment
      : treatments.find((option) => option.isRecommended)?.treatment) ??
    null;
  // Moved there already: on the books, solid, and nothing to choose - said
  // so under the pointer.
  if (moved) {
    return <TriageMovedChip testId={testId} payment={moved} />;
  }
  return (
    <span
      data-selectable-row-inert=""
      // What its key opens, where it shows one.
      data-opens-by-key={hotkey ? "" : undefined}
      onClick={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.preventDefault()}
      className="flex"
    >
      <Popper
        isOpenState={[isOpen, setIsOpen]}
        isClickable={false}
        placement="bottom-start"
        floatingStyle={{ width: 320 }}
        focusRestoreRef={focusedRef}
        anchor={
          // Drawn as every chip of another business is: offered, how the
          // two settle it before the business; clicked, it is chosen.
          <TriageBusinessChip
            testId={testId}
            state="suggested"
            isOut={isOut}
            treatment={shown}
            business={business}
            endDecorator={
              hotkey ? (
                <HotkeyDecorator
                  variant="outlined"
                  // From the panel's search, after Escape leaves it.
                  hotkey={isSearchFocused ? `escape+${hotkey}` : hotkey}
                  color="inherit"
                />
              ) : undefined
            }
            onClick={() => {
              if (!isOpen) {
                focusedRef.current =
                  document.activeElement as HTMLElement | null;
              }
              setIsOpen(!isOpen);
            }}
          />
        }
      >
        <div data-testid={`${testId}-panel`}>
          <List<TriageSettlementWay>
            data={treatments}
            getRowKey={(option) => option.treatment}
            onItemClick={(option) => {
              setIsOpen(false);
              onChoose(option.treatment);
            }}
            autofocus
            fillHeight={false}
            size="sm"
          >
            <List.Items<TriageSettlementWay>>
              {(option: TriageSettlementWay) => (
                <List.Item value={option}>
                  <div
                    data-testid={`${testId}-option`}
                    data-treatment={option.treatment}
                    data-chosen={option.treatment === shown}
                    className="flex flex-col pl-1 pr-1"
                  >
                    <div className="flex items-center gap-2">
                      <Typography level="body-sm" className="font-medium">
                        {option.title}
                      </Typography>
                      {option.treatment === shown ? (
                        <Icon ReactIcon={CheckIcon} size="sm" />
                      ) : null}
                    </div>
                    <Typography
                      level="body-xs"
                      className="text-muted-foreground"
                    >
                      {option.description}
                    </Typography>
                  </div>
                </List.Item>
              )}
            </List.Items>
          </List>
        </div>
      </Popper>
    </span>
  );
}

/** Picked: a tick on the solid ground - a stop's, or a card's that is one. */
export function TriagePickedMark({ className }: { className?: string }) {
  return (
    <span
      aria-hidden
      data-testid="triage-card-stop-picked"
      className={cn(
        "w-4.5 h-4.5 rounded-sm bg-primary text-primary-foreground flex items-center justify-center shrink-0",
        className,
      )}
    >
      <Icon ReactIcon={CheckIcon} size="xs" />
    </span>
  );
}

/**
 * One of a card's stops - a mailbox to search, a business to forward to, an
 * account to post to: a button the width of the card, which does what the
 * stop is for (*onPress*) as it is clicked - or by Enter, which its badge
 * shows on the one the keyboard is on. Under the keyboard - or the pointer -
 * it stands on a deeper ground, the keyboard's pointed at by an arrow, and
 * pressed, darker again. One with nothing to do - a
 * teammate's mailbox to reconnect, a document in the dialog that previews it
 * - only takes the keyboard. One with buttons of its own (*trailing*) has them
 * at its end, beside it on its ground, in its badge's place. One standing
 * alone, outside the panel's suggestions - a document lined up beside a
 * payment in a dialog - is drawn the same, its clicks not the panel's. One of
 * a card's finds (*card*) is drawn as the card's suggestions are, a size down:
 * where it is over its title, its date and amount at their lines' ends, the
 * line that says more under them with its key at that line's end, and a ball
 * on the card's edge.
 */
export function TriageCardStop({
  stop,
  isActive,
  isLoading,
  leading,
  title,
  description,
  card,
  trailing,
  onPress,
  label,
  isGrounded = false,
  isAction = false,
  action = "Open",
  isPicked = false,
  standsAlone = false,
}: {
  stop: Pick<TriageOptionListItem, "key">;
  isActive: boolean;
  isLoading: boolean;
  leading?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  /** Drawn as a suggestion's card (``TriageCard``): what goes over its title, at its lines' ends and on its edge. */
  card?: Pick<
    Parameters<typeof TriageCard>[0],
    "crumbs" | "date" | "amount" | "titleAfter" | "marker"
  >;
  /** At its end, beside it on its ground in its badge's place: a chip, its own buttons. */
  trailing?: ReactNode;
  /** What it does, clicked or by Enter. */
  onPress?: () => void;
  /** What it is called, where its words do not say what it does. */
  label?: string;
  /** Its ground drawn by what holds it - it and what it opens to, as one. */
  isGrounded?: boolean;
  /**
   * Something to do - a search, a mailbox to connect, an address to copy -
   * not a suggestion or a find: its title in body text, not a title's.
   */
  isAction?: boolean;
  /** What Enter does to it, said on its key's button: "Search", "Preview". */
  action?: string;
  /** Picked - a find, an account or a record: its tick in its key's place, wherever the keyboard is. */
  isPicked?: boolean;
  /** Outside the panel's suggestions: a click on it neither picks the panel's option nor turns the keys to them. */
  standsAlone?: boolean;
}) {
  // What trails it, as wide as it is: its title is kept clear of it, and the
  // line under the title runs on under it.
  const [trailingSize, setTrailingSize] = useState({ width: 0, height: 0 });
  const measured = useResizeObserver((rect) =>
    setTrailingSize({ width: rect.width, height: rect.height }),
  );
  // Its title run on past its first line - beside what trails it, onto the
  // next: that line goes under what trails it, not into it.
  const [isTitleWrapped, setIsTitleWrapped] = useState(false);
  const titleMeasured = useResizeObserver((rect) => {
    const element = titleMeasured.current as HTMLElement | null;
    const line = element ? parseFloat(getComputedStyle(element).lineHeight) : 0;
    setIsTitleWrapped(line > 0 && rect.height > 2 * line - 1);
  });
  // The panel it is a stop of - none, or not its, where it stands alone.
  const detail = useContext(TriageDetailContext);
  // Its key on a button the size and ground of a suggestion's Select - spans,
  // inside the stop's own button - kept in its place on the others, so no row
  // moves as it goes. Picked, the key is hidden and its tick - drawn as every
  // picked option's box is, solid and neutral - stands in its place.
  const key =
    onPress && !trailing ? (
      <span className="relative flex">
        <span
          ref={measured}
          data-testid="triage-card-stop-key"
          className={cn(
            TRIAGE_WAY_ACTIONS_CLASS,
            "flex",
            isActive && !isPicked ? "visible" : "invisible",
          )}
        >
          {/* A span drawn as a button: it sits inside the stop's own. */}
          <Button asChild variant="secondary" size="sm" tabIndex={-1}>
            <span>
              {action}
              {/* From the panel's search, after Escape leaves it. */}
              <HotkeyDecorator
                variant="soft"
                hotkey={detail?.isSearchFocused ? "escape+enter" : "enter"}
                color="inherit"
              />
            </span>
          </Button>
        </span>
        {isPicked ? (
          <TriagePickedMark className="absolute right-0 top-[50%] transform-[translateY(-50%)]" />
        ) : null}
      </span>
    ) : undefined;
  const press = () => {
    if (!standsAlone) {
      detail?.setActiveOptionKey(stop.key);
      detail?.engageSuggestions();
    }
    if (!isLoading) onPress?.();
  };
  const Stop = card ? "div" : "button";
  return (
    <div
      className={cn(
        "relative flex items-center min-w-0 rounded-md",
        // The card's own ground, the keys on it or not, pressed or not - the
        // line down its left edge says the one the keys are on - and a deeper
        // one under the pointer, partly seen through to the card under it.
        !isGrounded && [
          "bg-transparent",
          "hover:bg-[color-mix(in_srgb,var(--accent)_60%,transparent)]",
        ],
      )}
    >
      {/* The one the keyboard is on, marked by a line down its left edge,
          centred on it - half on its ground, half off - nothing on the stop
          moved by it. */}
      <div
        data-testid="triage-card-stop-mark"
        aria-hidden
        className={cn(
          "absolute [left:-1.5px] top-2.5 bottom-2.5 w-0.75 rounded-sm bg-muted-foreground pointer-events-none",
          isActive ? "opacity-[1]" : "opacity-[0]",
          "transition-normal [transition-duration:160ms] [transition-timing-function:ease-out] delay-[0s] [transition-property:opacity] motion-reduce:transition-normal [@media(prefers-reduced-motion:_reduce)]:[transition-duration:0s] [@media(prefers-reduced-motion:_reduce)]:[transition-timing-function:ease] motion-reduce:delay-[0s] motion-reduce:transition-none",
        )}
      />
      {/* A card, not a control at a size: what it is pressed as is its own
          (a button, or a box that acts as one), and how the keyboard on it
          looks - a button's ring. */}
      <Slot.Root
        data-testid="triage-card-stop"
        data-stop={stop.key}
        data-active={isActive}
        aria-label={label}
        onMouseDown={(event) => event.preventDefault()}
        // A click on its card, as any there is - and the keyboard on it, so
        // the keys it shows are its.
        onClick={press}
        className={cn(
          "flex-1 min-w-0",
          // Its ground at the card's inset, its icon under the card's
          // (``TRIAGE_STOP_ICON_INSET``) - drawn as a card, its words where
          // the card's are, and its ball on the card's edge where a
          // suggestion's is; its right edge as far in as what it opens to
          // under it.
          card ? "pl-1.5 pr-1.5" : "pl-2 pr-4",
          "pt-2 pb-2 inline-flex shrink-0 items-center gap-2 justify-start text-left text-sm font-medium rounded-md text-foreground",
          "outline-none transition-all focus-visible:ring-[3px] focus-visible:ring-ring/50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
          // Its ground is the row's, round its buttons too.
          "relative",
        )}
      >
        {/* Drawn as a card, a link of its own is on it - its overline's -
            which a button may not hold: a box that acts as one, as a
            suggestion's card is, pressed by Enter and Space as a button is. */}
        <Stop
          {...(card
            ? {
                role: "button",
                tabIndex: 0,
                onKeyDown: (event: KeyboardEvent<HTMLElement>) => {
                  if (event.target !== event.currentTarget) return;
                  if (event.key !== "Enter" && event.key !== " ") return;
                  event.preventDefault();
                  press();
                },
              }
            : { type: "button" as const })}
        >
          {card ? (
            <TriageCard
              {...card}
              isLoading={isLoading}
              isNested
              title={title}
              tools={
                <>
                  {description ? (
                    <Typography
                      level="body-sm"
                      data-testid="triage-card-stop-under"
                      className="truncate text-muted-foreground/80 min-w-0 flex-1"
                    >
                      <Skeleton loading={isLoading} variant="inline">
                        {description}
                      </Skeleton>
                    </Typography>
                  ) : null}
                  {key ? (
                    // Of no height on its line: centred on it, the line no
                    // taller than its words.
                    <span className="ml-auto flex items-center h-0 overflow-visible">
                      {key}
                    </span>
                  ) : null}
                </>
              }
              testIds={{
                crumbs: "triage-card-stop-overline",
                date: "triage-card-stop-overline-end",
                title: "triage-card-stop-title",
                amount: "triage-card-stop-title-end",
              }}
            />
          ) : (
            <div
              // Its own icon, its title, and under the title the line that says
              // more, as wide as the stop - the icon in the card's own icon
              // column, under the card's icon, so each title starts where the
              // card's title does, and what one opens to under it.
              // Its icon centred under the card's, its title starting where the
              // card's does: the card's 20px icon column less its padding either
              // side, and the gap after it more by as much.
              // Its line under its title held a little off it.
              className="flex-1 min-w-0 grid grid-cols-[16px_minmax(0,1fr)] gap-x-3 gap-y-1 items-center"
            >
              <div className="col-1 row-1 relative flex items-center justify-center">
                <Skeleton
                  loading={isLoading}
                  variant="rectangular"
                  className="w-fit max-w-full flex [flex-direction:inherit] [flex-wrap:inherit] [align-items:inherit] [justify-content:inherit] gap-[inherit]"
                >
                  {leading}
                </Skeleton>
              </div>
              <Typography
                ref={titleMeasured}
                level={isAction ? "body-md" : "title-md"}
                component="div"
                // A word with no break in it - an email's subject of code, a long
                // address - breaks where it must, rather than widen the card and
                // the list past the screen.
                className="col-2 row-1 min-w-0 wrap-anywhere"
              >
                {/* Clear of what trails it in its corner - its buttons, or its
                  key's - on its first line alone: a title longer than the room
                  beside them runs on under them, the stop's whole width, rather
                  than down a column a few letters wide on a narrow card. */}
                {trailing || onPress ? (
                  <span
                    aria-hidden
                    className={cn(
                      "float-right w-(--width)",
                      isTitleWrapped ? "h-(--height)" : "h-lh",
                    )}
                    style={
                      {
                        "--width":
                          Number(`${trailingSize.width}px`) <= 1
                            ? `${Number(`${trailingSize.width}px`) * 100}%`
                            : `${trailingSize.width}px`,
                        "--height": isTitleWrapped
                          ? Number(`calc(${trailingSize.height}px + 4px)`) <= 1
                            ? `${Number(`calc(${trailingSize.height}px + 4px)`) * 100}%`
                            : `calc(${trailingSize.height}px + 4px)`
                          : undefined,
                      } as CSSProperties
                    }
                  />
                ) : null}
                <Skeleton loading={isLoading} variant="inline">
                  {title}
                </Skeleton>
              </Typography>
              {description ? (
                <Typography
                  level="body-sm"
                  data-testid="triage-card-stop-under"
                  className="truncate col-2 row-2 text-muted-foreground/80"
                >
                  <Skeleton loading={isLoading} variant="inline">
                    {description}
                  </Skeleton>
                </Typography>
              ) : null}
            </div>
          )}
          {/* Its own end, at its far edge - not the end of the key's button
            inside it, which keeps its gap from its word - in its top corner,
            level with its title, over the stop, as what trails another is,
            so the line under the title runs on under it; as far in from its
            edge as down from its top. */}
          {card || !key ? null : (
            <span className="absolute top-2 right-2">{key}</span>
          )}
        </Stop>
      </Slot.Root>
      {trailing ? (
        <div
          ref={measured}
          // In its top corner, level with its title - as far down as the
          // title is from the stop's top - over the stop, so the line under
          // the title runs on under it; as far in from its edge as down from
          // its top. Its room a click on the stop; its buttons their own.
          className="flex items-center gap-2 absolute top-0 right-0 pt-2 pr-2 pointer-events-none [&>*]:pointer-events-auto"
        >
          {trailing}
        </div>
      ) : null}
    </div>
  );
}

/**
 * How much further in than its stops' grounds a card's icon and title are, in
 * the theme's spacing: each stop's icon and title as far into its ground.
 */
export const TRIAGE_STOP_ICON_INSET = 0.75;

/**
 * A row's buttons on a card of its kind - Move, View and Select, and the key
 * of a stop with nothing else to press - a size down from the row's title:
 * buttons, their keys and a tick alike.
 */
export const TRIAGE_WAY_ACTIONS_CLASS = [
  "[&_[data-slot=button]]:h-6.5 [&_[data-slot=button]]:text-xs [&_[data-slot=button]]:pl-2 [&_[data-slot=button]]:pt-0 [&_[data-slot=button]]:pb-0",
  // Close to the key after its word...
  "[&_[data-slot=button]]:pr-1",
  // ...and, with none, as far from its end as from its start.
  "[&_[data-slot=button]:not(:has(>[data-testid=hotkey-decorator]))]:pr-2",
  "[&_[data-testid='hotkey-decorator']]:text-[12px] [&_[data-testid='hotkey-decorator']]:min-w-4.5 [&_[data-testid='hotkey-decorator']]:h-4.5 [&_[data-testid='hotkey-decorator']]:pl-0.5 [&_[data-testid='hotkey-decorator']]:pr-0.5",
  "[&_:is([data-testid='triage-proposal-selection'],[data-testid='triage-proposal-booked'])]:w-6.5 [&_:is([data-testid='triage-proposal-selection'],[data-testid='triage-proposal-booked'])]:min-w-6.5 [&_:is([data-testid='triage-proposal-selection'],[data-testid='triage-proposal-booked'])]:h-6.5 [&_:is([data-testid='triage-proposal-selection'],[data-testid='triage-proposal-booked'])]:min-h-6.5",
  "[&_[data-testid='triage-proposal-booked']]:w-auto [&_[data-testid='triage-proposal-booked']]:pl-1.5 [&_[data-testid='triage-proposal-booked']]:pr-1.5",
  "[&_svg]:text-[1rem]",
].join(" ");

/**
 * A card of the payment's suggestions: one suggestion - drawn as the queue
 * draws a payment, a document as a document is wherever it is offered - or a
 * draft in review it would take, or, headed by its kind, what a search found
 * for what was typed or in the mailboxes. A suggestion or a draft is picked by
 * a click or Enter, and one with anything to set beside it opens under itself
 * to what it was compared with - by its chevron, or →.
 */
export function TriageSuggestionCard({
  item,
  isActive = false,
  isLoading = false,
  onActivateFound,
}: {
  item: TriageOptionListItem;
  isActive?: boolean;
  isLoading?: boolean;
  /**
   * What one of what it found for what is typed does, clicked or by Enter - a
   * way to post it not made yet given a category named on its chip
   * (*named*): made as it is booked, in place of the one it would make.
   */
  onActivateFound?: (stop: TriageOptionListItem, named?: string) => void;
}) {
  const group = item.group!;
  const {
    triage,
    activeOptionKey,
    setActiveOptionKey,
    selections,
    setSelection,
    isSearchFocused,
    expandedOptionKeys,
    setOptionExpanded,
    engageSuggestions,
  } = useTriageDetailContext();
  // Paid out, rather than taken in: which way another business's arrow points.
  const isOut = Number(triage.amount ?? 0) < 0;
  // A stop's key, as the keyboard where it is presses it: from the panel's
  // search, after Escape leaves it.
  const nameKey = (key: string) => (isSearchFocused ? `escape+${key}` : key);
  // A stop that opens to anything: a chevron in its icon's place, that opens
  // or shuts it - nothing picked - as a list's disclosure does.
  const drawDisclosure = (key: string, isExpanded: boolean) => (
    <span
      role="button"
      aria-label={isExpanded ? "Close" : "Open"}
      aria-expanded={isExpanded}
      data-testid="triage-card-stop-disclosure"
      onMouseDown={(event) => event.preventDefault()}
      onClick={(event) => {
        event.stopPropagation();
        setActiveOptionKey(key);
        setOptionExpanded?.(key, !isExpanded);
      }}
      // After its card's title, on its line.
      className={cn(
        "inline-flex align-middle ml-2 text-muted-foreground/80 cursor-pointer hover:text-foreground [&_svg]:[transition:transform_160ms_ease-out]",
        isExpanded
          ? "[&_svg]:transform-[rotate(90deg)]"
          : "[&_svg]:transform-none",
      )}
    >
      <Icon ReactIcon={ChevronRightIcon} size="sm" />
    </span>
  );
  // A card of it - a suggestion, a draft - drawn as the queue draws a
  // payment, so the two read across: a click on it puts the keyboard on it
  // and does what pressing it does (*onPress*) - picks it, or opens it.
  const drawCardStop = (
    key: string,
    isOn: boolean,
    onPress: (() => void) | undefined,
    card: ReactNode,
  ) => (
    <div
      data-testid="triage-card-stop"
      data-stop={key}
      data-active={isOn}
      role="button"
      tabIndex={-1}
      onMouseDown={(event) => event.preventDefault()}
      onClick={() => {
        setActiveOptionKey(key);
        engageSuggestions();
        if (!isLoading) onPress?.();
      }}
      className={cn("min-w-0", onPress ? "cursor-pointer" : "cursor-default")}
    >
      {card}
    </div>
  );
  const { triggerAccept } = useTriageViewActions();
  // What a row's View opens: a duplicate's two payments side by side, or what
  // a posting's account has taken lately - each booked from there.
  const [comparing, setComparing] = useState<TriageSuggestionStub | null>(null);
  const [previewing, setPreviewing] = useState<{
    suggestion: TriageSuggestionStub;
    account: TriagePostedAccount;
  } | null>(null);
  // Picked, and booked as Book books it once the pick is in - whenever that
  // is drawn, not a frame after it was asked for.
  const [bookingOnPick, setBookingOnPick] = useState<string | null>(null);
  useEffect(() => {
    if (
      bookingOnPick == null ||
      selections.linesSuggestionIdentifier !== bookingOnPick
    ) {
      return;
    }
    setBookingOnPick(null);
    // After the view's own effects have taken the pick in.
    requestAnimationFrame(() => triggerAccept());
  }, [bookingOnPick, selections.linesSuggestionIdentifier, triggerAccept]);
  const book = (chosen: TriageSuggestionStub) => {
    if (selections.linesSuggestionIdentifier !== chosen.identifier) {
      toggleLinesSuggestion(chosen, null, setSelection);
    }
    setBookingOnPick(chosen.identifier);
  };
  const { size, layout } = useSelectableListStyle();
  const inset = getSelectableRowInset(size, layout);

  // Its suggestions, what it found for what is typed, then - on the Match
  // card - the ways to its document in a mailbox.
  const ways = (item.alternatives ?? []).filter((way) => way.suggestion);
  // On the Match card, first, the drafts in review it would take.
  const drafts = (item.alternatives ?? []).filter(
    (way) => way.sectionId === "drafts",
  );
  const found = (item.alternatives ?? []).filter((way) => way.isFound);
  const mailboxStops = (item.alternatives ?? []).filter(
    (way) => !way.suggestion && !way.isFound && way.sectionId !== "drafts",
  );
  // The keyboard on it with nothing picked to book: ⌘↵ works its kind's
  // suggestions out again by their rules, as its kind's Think in the bar says
  // - picked, ⌘↵ books.
  const isThinkingByKey =
    isActive &&
    !isLoading &&
    selections.linesSuggestionIdentifier == null &&
    !(item.alternatives ?? []).some((way) => way.selected);
  return (
    <div
      data-testid="triage-suggestion-card"
      data-group={group.id}
      data-active={isActive}
      data-thinks-by-key={isThinkingByKey ? "" : undefined}
      // A card of what a search found, its kind's name over it.
      data-headed={item.isHeaded ? "" : undefined}
      onMouseDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
      // Its icon and title in from its edge by a little more than its stops'
      // grounds are (``TRIAGE_STOP_ICON_INSET``), so theirs, under its, sit
      // that far into their grounds.
      // A suggestion's card is set as the queue's row is: its words at the
      // list's inset.
      className={cn(
        "grid grid-cols-[20px_minmax(0,1fr)_auto] gap-x-2.5 [align-items:start] gap-y-2",
        item.isHeaded ? "pl-(--padding-left-2)" : "pl-(--px)",
        // Its words a little nearer its top than its sides, as the queue's.
        "pr-(--px) pt-(--pt) pb-(--py)",
      )}
      style={
        {
          "--padding-left-2": item.isHeaded
            ? `${(inset.px + TRIAGE_STOP_ICON_INSET) * 8}px`
            : undefined,
          "--px": `${inset.px * 8}px`,
          "--pt": `${(inset.py - 0.5) * 8}px`,
          "--py": `${inset.py * 8}px`,
        } as CSSProperties
      }
    >
      {/* A card of what a search found - for what is typed, or in the
          mailboxes - its kind's icon and name over it ("Match", "Post"), the
          words it was searched for beside them, and in its corner a spinner
          till what they found is in. */}
      {item.isHeaded ? (
        <>
          <Skeleton
            loading={isLoading}
            variant="rectangular"
            className="w-fit max-w-full flex [flex-direction:inherit] [flex-wrap:inherit] [align-items:inherit] [justify-content:inherit] gap-[inherit] col-1 self-center"
          >
            <div className="col-1 self-center flex justify-center text-muted-foreground/80">
              <Icon
                ReactIcon={JOB_ICONS[group.jobs[0] as TriageJobEnum]}
                size="lg"
              />
            </div>
          </Skeleton>
          <Typography level="title-lg" className="col-2 self-center min-w-0">
            <Skeleton loading={isLoading} variant="inline">
              {group.title}
            </Skeleton>
            {/* The words it is searched for, kept as the keyboard moves on. */}
            {item.searchedFor && !isLoading ? (
              <Typography
                component="span"
                level="body-lg"
                data-testid="triage-suggestion-card-searched-for"
                className="text-muted-foreground/80 ml-1.5"
              >
                {item.searchedFor.map((word) => `“${word}”`).join(" ")}
              </Typography>
            ) : null}
          </Typography>
          {item.isSearching ? (
            <div
              // Of no height in the row: centred on the title's line, so drawn
              // or not, the title row is one height.
              className="col-3 row-1 self-center flex h-0 items-center overflow-visible"
            >
              <Spinner data-testid="triage-suggestion-card-searching" />
            </div>
          ) : null}
        </>
      ) : null}
      <div
        // From under the card's icon: each one's icon under it, its title
        // under the card's.
        // Under its heading, its first a little further from it than the
        // rows are from each other: under it, not one of it. Its grounds at
        // the card's inset, out from under its icon.
        // Nothing on the card: no row of its own, and no gap over it - its
        // title as far from its foot as from its top.
        className={cn(
          "col-span-full flex flex-col gap-0.5 min-w-0",
          item.isHeaded ? "mt-1" : "mt-0",
          item.isHeaded ? "-ml-1.5" : "ml-0",
          "empty:hidden",
        )}
      >
        {drafts.map((draft) => {
          const isOn = isActive && draft.key === activeOptionKey;
          const pick = () => {
            setActiveOptionKey(draft.key);
            commitActiveOption(draft.key, setSelection);
          };
          // Opened under itself - by its chevron or → - to what it drafted.
          const canOpen = draft.draft?.document?.pendingChange != null;
          const isExpanded = expandedOptionKeys?.includes(draft.key) ?? false;
          const opens = canOpen && isExpanded;
          return (
            // It and what it opens to on one ground, as a suggestion's.
            <div
              key={draft.key}
              data-testid="triage-draft-match"
              data-active={isOn ? "true" : "false"}
              data-option-key={draft.key}
              data-expandable={canOpen ? "" : undefined}
              // The card itself, what it opens to inside it: no ground of its
              // own - the card's outline says the keyboard is on it.
              className="min-w-0"
            >
              {drawCardStop(
                draft.key,
                isOn,
                // A click on it opens it or shuts it, as its chevron does.
                canOpen
                  ? () => setOptionExpanded?.(draft.key, !isExpanded)
                  : undefined,
                <TriageDraftMatchStop
                  stop={draft}
                  isActive={isOn}
                  // Booked only once accepted, as the suggestion it makes:
                  // its tick is out of reach till then.
                  selectionControl={
                    isOn || draft.selected ? (
                      <TriageOptionSelectionControl
                        item={draft}
                        disabled
                        variant="secondary"
                        onToggle={pick}
                      />
                    ) : null
                  }
                  disclosure={
                    canOpen ? drawDisclosure(draft.key, isExpanded) : undefined
                  }
                />,
              )}
              <AnimatePresence initial={false}>
                {opens ? (
                  <motion.div
                    key="details"
                    // Clipped only while it slides: open, it is as tall as
                    // what it holds - what loads after, its line items, too.
                    initial={{ height: 0, opacity: 0, overflow: "hidden" }}
                    animate={{
                      height: "auto",
                      opacity: 1,
                      transitionEnd: { height: "auto", overflow: "visible" },
                    }}
                    exit={{ height: 0, opacity: 0, overflow: "hidden" }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                  >
                    <TriageDraftMatchDetails item={draft} />
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </div>
          );
        })}
        {ways.map((way) => {
          const suggestion = way.suggestion!;
          const presentation = buildChangelistProposalPresentation(
            suggestion,
            triage,
          );
          // A posting by the accounts it posts to, where each sits under
          // them; the rest by what they do.
          const accounts =
            presentation.job === "CATEGORIZE"
              ? proposalSuggestedAccounts(suggestion, triage)
              : [];
          // What it was set beside: the documents it would settle, the other
          // payment.
          const reasons = triageSuggestionReasons(suggestion, triage);
          // Named as what it was set beside is named - as what a search found
          // is - a match by its documents' names, a posting by its accounts;
          // else by what it does.
          const title =
            accounts.length > 0
              ? accounts
                  .map((account) =>
                    account.isNew ? `${account.name} (new)` : account.name,
                  )
                  .join(", ")
              : presentation.job === "MATCH_DOCUMENT" &&
                  presentation.linkedDocuments.length > 0
                ? `Match ${presentation.linkedDocuments
                    .map((linked) => `“${linked.title}”`)
                    .join(", ")}`
                : reasons.comparisons.length > 0
                  ? reasons.comparisons
                      .map((comparison) => comparison.title)
                      .join(", ")
                  : proposalCardTitleParts(suggestion, presentation, triage)
                      .map((part) => ("text" in part ? part.text : part.name))
                      .join("");
          // Whose and where, over its name, as the queue's row says it: a
          // match its document's business; a posting the business it posts
          // in and where in its chart; a copy or a transfer the other
          // payment's business and bank account - nothing of the rule.
          const posted =
            accounts.length > 0
              ? offsetLines(suggestion, triage).find(
                  (line) =>
                    !INTER_ENTITY_ACCOUNTS.has(
                      line.chartedAccount?.name?.trim() ?? "",
                    ),
                )
              : null;
          const other = (suggestion.ruleSuggestionTransactionOptions ?? [])
            .map((option) => option.transaction)
            .find(Boolean);
          const settlesDocument = (
            suggestion.ruleSuggestionDocumentOptions ?? []
          ).find((option) => option.document)?.document;
          const crumbs: Array<{ nodeId?: string | null; label: string }> =
            presentation.job === "MATCH_DOCUMENT"
              ? settlesDocument?.entity?.name
                ? [
                    {
                      nodeId: settlesDocument.entity.id,
                      label: settlesDocument.entity.name,
                    },
                  ]
                : []
              : accounts.length > 0
                ? [
                    ...(posted?.entity?.name
                      ? [
                          {
                            nodeId: posted.entity.id,
                            label: posted.entity.name,
                          },
                        ]
                      : []),
                    ...[
                      ...new Set(
                        accounts.map((account) => account.path).filter(Boolean),
                      ),
                    ].map((path) => ({ label: path })),
                  ]
                : other
                  ? [
                      ...(other.entity?.name
                        ? [
                            {
                              nodeId: other.entity.id,
                              label: other.entity.name,
                            },
                          ]
                        : []),
                      ...(other.financialAccount?.name
                        ? [
                            {
                              nodeId: other.financialAccount.id,
                              label: other.financialAccount.name,
                            },
                          ]
                        : []),
                    ]
                  : [];
          const isPicked =
            selections.linesSuggestionIdentifier === suggestion.identifier;
          const isBooked =
            suggestion.status === TriageSuggestionStatusEnum.Accepted;
          const isOn = isActive && way.key === activeOptionKey;
          // What it is looked over by before it is taken: a match's
          // document on its preview, a duplicate beside its copy, what a
          // posting's account has taken lately.
          const document =
            presentation.job === "MATCH_DOCUMENT"
              ? (suggestion.ruleSuggestionDocumentOptions ?? []).find(
                  (option) => option.document,
                )?.document
              : null;
          const account =
            presentation.job === "CATEGORIZE"
              ? postedAccountOf(suggestion, triage)
              : null;
          const view = document
            ? () =>
                State.getState().pushToCommandStack({
                  commandDefinitionId: "update-DocumentObject",
                  context: {
                    nodeId: document.id,
                    entityIdentifier:
                      document.entity?.identifier ?? triage.entity?.identifier,
                    initialGroupId: "preview",
                    action: {
                      label: "Book",
                      onPress: () => book(suggestion),
                    },
                  },
                })
            : presentation.job === "DUPLICATE" &&
                (suggestion.ruleSuggestionTransactionOptions ?? []).some(
                  (option) => option.transaction,
                )
              ? () => setComparing(suggestion)
              : account
                ? () => setPreviewing({ suggestion, account })
                : null;
          const pick = () => {
            toggleLinesSuggestion(
              suggestion,
              selections.linesSuggestionIdentifier,
              setSelection,
            );
            setActiveOptionKey(way.key);
          };
          // Opened under itself - by its chevron, a click on it, or →, not by
          // being picked - to its figures beside what they were compared
          // with: the documents it would settle, the other payment. One with
          // nothing to compare does not open.
          const canOpen =
            reasons.job === "MATCH_DOCUMENT"
              ? reasons.presentation.linkedDocuments.length > 0
              : reasons.comparisons.length > 0;
          const isExpanded = expandedOptionKeys?.includes(way.key) ?? false;
          const opens = canOpen && isExpanded;
          // Under its name, as under every row: its date, its amount, its
          // words - and where a posting sits, and that it is booked.
          const findCompared = (about: "date" | "amount" | "description") =>
            reasons.comparisons[0]?.rows.find((one) => one.about === about);
          // What it says of one, as the table it opens to gives it - none
          // where it says nothing.
          const findShown = (about: "date" | "amount") => {
            const row = findCompared(about);
            return row?.other && row.other !== "—" ? row.other : undefined;
          };
          // Its words as the table it opens to gives them: a document's
          // number, then its name; the other payment's description - not
          // again where its name already says them.
          const settled =
            presentation.job === "MATCH_DOCUMENT"
              ? presentation.linkedDocuments[0]
              : null;
          const wording = findCompared("description");
          // A document's number - its name is its card's title.
          // A match's number is its View chip's.
          const words = settled
            ? null
            : wording?.other !== "—"
              ? wording?.other
              : null;
          // A match settled in another business than its document's: the
          // document goes there with it - said under it, as the queue's row
          // says another business's half, with how the two settle it.
          const movesDocumentTo =
            presentation.job === "MATCH_DOCUMENT" &&
            presentation.otherBusiness &&
            settlesDocument?.entity?.identifier !==
              presentation.otherBusiness.identifier
              ? presentation.otherBusiness
              : null;
          // How the two businesses settle it, changed here - picking it, if
          // it was not.
          const settlementChip = presentation.otherBusiness ? (
            <TriageSettlementChip
              suggestion={suggestion}
              business={presentation.otherBusiness.name}
              isOut={Number(triage.amount ?? 0) < 0}
              chosen={isPicked ? selections.settlementTreatment : null}
              // Moved there already: settled as it was moved, nothing more
              // for this to do.
              moved={
                triage.belongsToEntity?.identifier ===
                presentation.otherBusiness.identifier
                  ? triage
                  : null
              }
              onChoose={(treatment) => {
                if (!isPicked) {
                  toggleLinesSuggestion(
                    suggestion,
                    selections.linesSuggestionIdentifier,
                    setSelection,
                  );
                }
                setSelection("settlementTreatment", treatment);
              }}
            />
          ) : null;
          const under: Array<{ key: string; text: string }> = [
            ...(words && words !== title
              ? [{ key: "description", text: words }]
              : []),
            // The business it books in, where that is another than the
            // payment's own - moved there, or settled there - before where in
            // its chart: whose account it is, said.
            ...(presentation.otherBusiness?.name
              ? [{ key: "business", text: presentation.otherBusiness.name }]
              : []),
            ...(isBooked ? [{ key: "booked", text: "Booked" }] : []),
          ];
          // Picked, a ball on its card's left edge, level with its title - the
          // end the line from its payment in the queue comes to
          // (``TriageLinks``). Pressed, it lets it go.
          const ball = isBooked ? undefined : (
            <TriageLinkBall
              link="picked"
              end="to"
              isEngaged={isPicked}
              onPress={pick}
              label={isPicked ? "Selected" : "Select"}
              testId="triage-proposal-confirm"
            />
          );
          // Its Select and View - booked, its booked mark.
          const actions = isBooked ? (
            <div className={cn("flex", TRIAGE_WAY_ACTIONS_CLASS)}>
              <TriageOptionSelectionControl
                item={way}
                variant="secondary"
                onToggle={pick}
              />
            </div>
          ) : undefined;
          const keyboardView =
            view && isOn ? (
              <button
                type="button"
                hidden
                data-testid="triage-proposal-view"
                // Looked at, not picked: not a click on its card.
                onClick={(event) => {
                  event.stopPropagation();
                  view();
                }}
              />
            ) : null;
          // Slid open under its top row - over where it goes with the
          // payment, which stays its card's last word - and shut. Inside its
          // stop, it is read, not pressed: a click or a press in it is its
          // own, picking nothing and keeping the selection it makes.
          const details = (
            <AnimatePresence initial={false}>
              {opens ? (
                <motion.div
                  key="details"
                  onMouseDown={(event) => event.stopPropagation()}
                  onClick={(event) => event.stopPropagation()}
                  className="cursor-default"
                  // Clipped only while it slides: open, it is as tall as
                  // what it holds - what loads after, its line items, too.
                  initial={{ height: 0, opacity: 0, overflow: "hidden" }}
                  animate={{
                    height: "auto",
                    opacity: 1,
                    transitionEnd: { height: "auto", overflow: "visible" },
                  }}
                  exit={{ height: 0, opacity: 0, overflow: "hidden" }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                >
                  <div
                    // Inside the card's own padding, as far in from its
                    // edge on either side as its words are: not up
                    // against the card.
                    className="pt-0.5"
                  >
                    <div
                      data-testid="triage-suggestion-card-details"
                      // Set well off the row over it, its table ending at
                      // its last row.
                      // A step off the card's own ground, rounded, tinted
                      // and edged as the chips over it are
                      // (``TRIAGE_ROW_FOOT_CHIP_CLASS``).
                      className="min-w-0 mt-1.5 pt-1.5 rounded-xl overflow-hidden border border-border bg-muted dark:bg-secondary cursor-default"
                    >
                      <TriageSuggestionContext
                        suggestion={suggestion}
                        triage={triage}
                      />
                    </div>
                  </div>
                </motion.div>
              ) : null}
            </AnimatePresence>
          );
          return (
            // It and what it opens to together - the keys' stop marked by
            // the line down its left edge.
            <div
              key={way.key}
              data-option-key={way.key}
              data-expandable={canOpen ? "" : undefined}
              // The card itself, what it opens to inside it: no ground of its
              // own - the card's outline says the keyboard is on it.
              className="min-w-0"
            >
              {/* A click on it picks it, or lets it go - Enter as well; its
                  chevron, or →, opens it. */}
              {drawCardStop(
                way.key,
                isOn,
                pick,
                presentation.job === "MATCH_DOCUMENT" &&
                  settlesDocument &&
                  (settled?.title ?? settlesDocument.name) ? (
                  // A document: drawn as a document is wherever it is offered
                  // the payment - here, or lined up in Similar.
                  <TriageDocumentCard
                    isLoading={isLoading}
                    document={{
                      name: (settled?.title ?? settlesDocument.name)!,
                      reference: settlesDocument.reference ?? null,
                      date: settlesDocument.dateIssued ?? null,
                      amount:
                        settlesDocument.amountTotal != null
                          ? Number(settlesDocument.amountTotal)
                          : null,
                      currency: settlesDocument.amountCurrency ?? null,
                      business: settlesDocument.entity ?? null,
                      contact: settlesDocument.contact ?? null,
                    }}
                    businessIdentifier={triage.entity?.identifier}
                    onView={view ?? undefined}
                    viewHotkey={nameKey("space")}
                    businessChip={
                      presentation.otherBusiness ? settlementChip : undefined
                    }
                    movesTo={movesDocumentTo}
                    isStaged={isPicked}
                    details={details}
                    titleAfter={
                      canOpen ? drawDisclosure(way.key, isExpanded) : null
                    }
                    toolsAfter={
                      actions ? (
                        <div className="ml-auto flex">{actions}</div>
                      ) : null
                    }
                    marker={
                      <>
                        {ball}
                        {keyboardView}
                      </>
                    }
                    testIds={{
                      crumbs: "triage-card-stop-overline",
                      date: "triage-card-stop-overline-end",
                      title: "triage-card-stop-title",
                      amount: "triage-card-stop-title-end",
                      tools: "triage-card-stop-under",
                    }}
                  />
                ) : (
                  <TriageCard
                    isLoading={isLoading}
                    crumbs={crumbs}
                    // Its date and amount where the queue's row has them.
                    date={findShown("date")}
                    amount={findShown("amount")}
                    title={
                      // Its name read as one: a name longer than the card is
                      // cut short. Clipped, not hidden: a box that hides what
                      // overflows it takes its foot for its baseline, and the
                      // category chip after it sat on that.
                      <span
                        data-testid="triage-proposal-title"
                        className="whitespace-nowrap inline-block max-w-full overflow-clip text-ellipsis align-baseline"
                      >
                        {title}
                      </span>
                    }
                    // Its chevron, where it opens to anything.
                    titleAfter={
                      canOpen ? drawDisclosure(way.key, isExpanded) : null
                    }
                    details={details}
                    marker={
                      <>
                        {ball}
                        {keyboardView}
                      </>
                    }
                    // Its line of chips, as the queue's row has: its document,
                    // what it was set beside, how it settles in another business
                    // - and booked, that it is.
                    tools={
                      // A posting's category chip - where it has anything to
                      // say - first; with nothing on it, the line is not drawn.
                      under.length > 0 ||
                      actions ||
                      presentation.job === "CATEGORIZE" ? (
                        <>
                          {/* What it files under, first on its line of chips -
                            C changes it. */}
                          {presentation.job === "CATEGORIZE" ? (
                            <TriageSuggestionCategoryChip
                              suggestion={suggestion}
                              isActive={isOn}
                              onPick={() =>
                                toggleLinesSuggestion(
                                  suggestion,
                                  selections.linesSuggestionIdentifier,
                                  setSelection,
                                )
                              }
                            />
                          ) : null}
                          {under.map(({ key, text }) =>
                            key === "business" ? (
                              <Fragment key={key}>{settlementChip}</Fragment>
                            ) : (
                              <Typography
                                key={key}
                                level="body-sm"
                                className="truncate text-muted-foreground/80 min-w-0"
                              >
                                {text}
                              </Typography>
                            ),
                          )}
                          {actions ? (
                            <div className="ml-auto flex">{actions}</div>
                          ) : null}
                        </>
                      ) : null
                    }
                    testIds={{
                      crumbs: "triage-card-stop-overline",
                      date: "triage-card-stop-overline-end",
                      title: "triage-card-stop-title",
                      amount: "triage-card-stop-title-end",
                      tools: "triage-card-stop-under",
                    }}
                  />
                ),
              )}
            </div>
          );
        })}
        {found.length > 0 ? (
          <>
            <Typography
              level="body-sm"
              data-testid="triage-suggestion-card-found"
              className={cn(
                "text-muted-foreground/80",
                ways.length > 0 ? "mt-2" : "mt-0",
              )}
            >
              Results
            </Typography>
            {found.map((stop) => {
              const isMore = stop.sectionId === "more";
              // The one row, where nothing was found: the whole list.
              const isNothing = isMore && found.length === 1;
              return (
                <TriageCardStop
                  key={stop.key}
                  isAction={isMore}
                  stop={stop}
                  isActive={isActive && stop.key === activeOptionKey}
                  isLoading={isLoading}
                  leading={
                    // Its kind's icon, picked or not. Not an arrow: the
                    // keyboard's comes in beside the one it is on.
                    <div className="flex text-muted-foreground/80">
                      {isNothing ? (
                        // The app's own glass, as its searches draw it.
                        <Icon ReactIcon={icons.search} size="sm" />
                      ) : (
                        <Icon
                          ReactIcon={
                            isMore
                              ? EllipsisIcon
                              : JOB_ICONS[group.jobs[0] as TriageJobEnum]
                          }
                          size="sm"
                        />
                      )}
                    </div>
                  }
                  title={
                    stop.sectionId === "accounts" ||
                    stop.sectionId === "new-ways" ? (
                      <>
                        {stop.title}
                        {/* What it files under - the category the words
                            name, made or to be made, or one chosen - C
                            changes it, as on a suggestion's. */}
                        <span
                          // On the title's baseline, its words on the
                          // title's line.
                          className="inline-flex align-baseline ml-2"
                        >
                          <TriageProposalCategoryChip
                            // An account not made yet has no categories to
                            // choose: one named is made under it.
                            account={
                              stop.sectionId === "accounts"
                                ? {
                                    identifier: stop.key.slice(
                                      "account:".length,
                                    ),
                                    name: String(stop.title),
                                  }
                                : null
                            }
                            suggested={
                              stop.foundCategory
                                ? {
                                    ...stop.foundCategory,
                                    color: (stop.foundCategory.color ??
                                      null) as TagColorEnum | null,
                                  }
                                : null
                            }
                            proposed={stop.way?.categoryName ?? null}
                            chosenIdentifier={
                              stop.selected
                                ? selections.categoryIdentifier
                                : null
                            }
                            named={
                              stop.selected ? selections.newCategoryName : null
                            }
                            onChoose={(identifier) => {
                              if (!stop.selected) onActivateFound?.(stop);
                              setSelection("categoryIdentifier", identifier);
                              setSelection("newCategoryName", null);
                            }}
                            // Named here, made under it as it is booked - an
                            // account not made yet made first, as it is when
                            // picked.
                            onName={(name) => {
                              if (stop.sectionId === "new-ways") {
                                onActivateFound?.(stop, name);
                                return;
                              }
                              if (!stop.selected) onActivateFound?.(stop);
                              setSelection("categoryIdentifier", null);
                              setSelection("newCategoryName", name);
                            }}
                            triage={triage}
                            isActive={isActive && stop.key === activeOptionKey}
                          />
                        </span>
                      </>
                    ) : (
                      stop.title
                    )
                  }
                  description={
                    isMore || stop.sectionId === "new-ways"
                      ? stop.description
                      : [
                          // Another business's, first - its arrow the way the
                          // money went; then its date and amount, as every
                          // row says them; an account's categories.
                          stop.entityIdentifier &&
                          stop.entityIdentifier !== triage.entity?.identifier &&
                          stop.breadcrumb?.[0]?.label ? (
                            <>
                              <span className="inline-flex align-middle mr-1">
                                <TriageBusinessArrow isOut={isOut} />
                              </span>
                              {stop.breadcrumb[0].label}
                            </>
                          ) : null,
                          stop.dateLabel,
                          stop.amountLabel,
                          ...(stop.categories ?? []).map(
                            (category) => category.name,
                          ),
                        ]
                          .filter(Boolean)
                          .map((part, index) => (
                            <Fragment key={index}>
                              {index > 0 ? " · " : null}
                              {part}
                            </Fragment>
                          ))
                  }
                  action={
                    isMore || stop.sectionId === "duplicates"
                      ? "Open"
                      : stop.sectionId === "accounts" ||
                          stop.sectionId === "new-ways"
                        ? stop.selected
                          ? "Selected"
                          : "Post"
                        : stop.selected
                          ? "Remove"
                          : "Add"
                  }
                  // Any find picked - an account, a document, a payment -
                  // its box ticked, as a picked suggestion's is.
                  isPicked={
                    stop.selected && !isMore && stop.sectionId !== "duplicates"
                  }
                  onPress={() => onActivateFound?.(stop)}
                />
              );
            })}
          </>
        ) : null}
        {mailboxStops.length > 0 || item.mailboxSearch?.isSearching ? (
          <>
            <Typography
              level="body-sm"
              data-testid="triage-suggestion-card-mailbox"
              className={cn(
                "text-muted-foreground/80",
                ways.length > 0 || found.length > 0 ? "mt-2" : "mt-0",
              )}
            >
              In your mailbox
            </Typography>
            {/* Searched for what is typed: three rows drawn loading in the
                place of what it finds. */}
            {item.mailboxSearch?.isSearching
              ? TRIAGE_MAILBOX_FIND_PLACEHOLDERS.map((placeholder) => (
                  <TriageSearchMailboxStop
                    key={placeholder.key}
                    stop={placeholder}
                    isLoading
                  />
                ))
              : null}
            {mailboxStops.map((stop) => (
              <TriageSearchMailboxStop
                key={stop.key}
                stop={stop}
                isActive={isActive && stop.key === activeOptionKey}
                isLoading={isLoading}
              />
            ))}
          </>
        ) : null}
      </div>
      <TriageDuplicateDialog
        triage={triage}
        suggestion={comparing}
        onClose={() => setComparing(null)}
        onBook={() => comparing && book(comparing)}
      />
      <TriageAccountPostingsDialog
        triageId={triage.id ?? ""}
        account={previewing?.account ?? null}
        onClose={() => setPreviewing(null)}
        onBook={() => previewing && book(previewing.suggestion)}
      />
    </div>
  );
}
